/**
 * Parse a canonical variant id of the form `chr<chrom>-<pos>-<ref>-<alt>` into
 * its parts. Examples:
 *   chr1-12345-A-G   -> { chrom: "chr1", pos: 12345, ref: "A", alt: "G" }
 *   chrX-9988776-T-C -> { chrom: "chrX", pos: 9988776, ref: "T", alt: "C" }
 *
 * Anything that does not match falls back to `null`, in which case callers can
 * decide to render a default whole-genome view.
 */

export type ParsedVariant = {
  chrom: string
  pos: number
  ref: string
  alt: string
}

const VARIANT_RE = /^(chr(?:[1-9]|1\d|2[0-2]|X|Y|M|MT))-(\d+)-([ACGTN]+)-([ACGTN]+)$/i

export function parseVariantId(variantId: string): ParsedVariant | null {
  const match = VARIANT_RE.exec(variantId.trim())
  if (!match) return null
  const [, rawChrom, rawPos, rawRef, rawAlt] = match
  return {
    chrom: rawChrom.toLowerCase().startsWith('chr') ? rawChrom : `chr${rawChrom}`,
    pos: Number.parseInt(rawPos, 10),
    ref: rawRef.toUpperCase(),
    alt: rawAlt.toUpperCase(),
  }
}

export function locusFromVariant(variant: ParsedVariant, flank = 100): string {
  const start = Math.max(1, variant.pos - flank)
  const end = variant.pos + flank
  return `${variant.chrom}:${start}-${end}`
}
